import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useChatUIStore } from "~/shared/store/useChatUIStore";

import { runValidationAndUpload } from "./runValidationAndUpload";
import type { KindLimits, ValidateAttachInput } from "./validateAndSliceFiles";

export interface UseChatDropZoneParams {
    chatId: string | null;
    /** Per-kind лимиты режима. null kind = режим его не принимает. */
    limits: KindLimits;
    /** Уже в pendingByChat для каждого kind. */
    currentCounts: { image: number; video: number; audio: number };
    /** AttachmentPolicy с бэка (mimeTypes/maxSizeMb для каждого kind). */
    policy: ValidateAttachInput["policy"];
    upload: (files: File[]) => Promise<unknown>;
    /** Пока true — drop игнорируется. */
    disabled?: boolean;
}

export interface DropZoneHandlers {
    onDragEnter: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
}

export interface UseChatDropZoneResult {
    isDragOver: boolean;
    handlers: DropZoneHandlers;
}

export function useChatDropZone({
    chatId,
    limits,
    currentCounts,
    policy,
    upload,
    disabled = false,
}: UseChatDropZoneParams): UseChatDropZoneResult {
    const { t } = useTranslation("chat");
    const [isDragOver, setIsDragOver] = useState(false);
    // dragenter/leave fire'ятся на каждый child element — без счётчика
    // overlay flicker'ит. Считаем глубину «вошёл/вышел» сами.
    const dragCounterRef = useRef(0);

    // Блокируем drop пока открыт picker «из загруженных»/«из сгенерированных»
    // — иначе одновременный drop файла и выбор из существующих создаёт
    // непредсказуемое поведение.
    const pickerOpen = useChatUIStore((s) => s.attachPickerOpen);

    const isDropAllowed = !disabled && !pickerOpen && !!chatId;

    // Window-level preventDefault — defensive дубль на будущее.
    // Сейчас эту же роль исполняет глобальный legacy-хук
    // `src/hooks/useDragDropPrevention` (подключён в App.tsx). Когда legacy
    // UI и его хук уйдут (Фаза N миграции), наш useEffect станет
    // единственной защитой от browser-default-open-file поведения для
    // файлов сброшенных мимо chat drop-зоны. Стоимость дубля — ноль,
    // снимать сейчас → словить баг через полгода без понятной причины.
    useEffect(() => {
        const onWindowDragOver = (e: DragEvent) => {
            e.preventDefault();
        };
        const onWindowDrop = (e: DragEvent) => {
            e.preventDefault();
        };
        window.addEventListener("dragover", onWindowDragOver);
        window.addEventListener("drop", onWindowDrop);
        return () => {
            window.removeEventListener("dragover", onWindowDragOver);
            window.removeEventListener("drop", onWindowDrop);
        };
    }, []);

    // Тащит ли юзер именно файлы (не текст / не нашу dnd-сортировку миниатюр).
    // Используется ТОЛЬКО чтобы решить, показывать ли overlay и dropEffect —
    // НЕ для решения «делать ли preventDefault». preventDefault на dragover
    // обязателен безусловно: иначе браузер не разрешит drop ВООБЩЕ.
    const isFilesDrag = useCallback((e: React.DragEvent): boolean => {
        return Array.from(e.dataTransfer.types).includes("Files");
    }, []);

    const onDragEnter = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            if (!isFilesDrag(e)) return;
            dragCounterRef.current += 1;
            if (dragCounterRef.current === 1 && isDropAllowed) setIsDragOver(true);
        },
        [isFilesDrag, isDropAllowed],
    );

    const onDragOver = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = isDropAllowed && isFilesDrag(e) ? "copy" : "none";
        },
        [isFilesDrag, isDropAllowed],
    );

    const onDragLeave = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            if (!isFilesDrag(e)) return;
            dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
            if (dragCounterRef.current === 0) setIsDragOver(false);
        },
        [isFilesDrag],
    );

    const onDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            dragCounterRef.current = 0;
            setIsDragOver(false);

            if (!isDropAllowed) return;

            const files = Array.from(e.dataTransfer.files);
            if (files.length === 0) return;

            void runValidationAndUpload({
                files,
                limits,
                currentCounts,
                policy,
                upload,
                t,
            });
        },
        [isDropAllowed, limits, currentCounts, policy, upload, t],
    );

    return {
        isDragOver: isDragOver && isDropAllowed,
        handlers: { onDragEnter, onDragOver, onDragLeave, onDrop },
    };
}
